import React from 'react';
import { motion } from 'framer-motion';
import { FullCaseDetails } from '../../../../interfaces/cliente.types';
import styles from './FinancialCard.module.css';
import { DollarSign, CheckCircle, Clock, PlusCircle } from 'lucide-react';
interface CasoFinancialCardProps {
    caso: FullCaseDetails;
    onAddPago: () => void;
}

export const CasoFinancialCard: React.FC<CasoFinancialCardProps> = ({ caso, onAddPago }) => {
    
    const contentVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { 
            opacity: 1, 
            y: 0,
            transition: { duration: 0.5, ease: 'easeOut', staggerChildren: 0.1 }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, scale: 0.95 },
        visible: { opacity: 1, scale: 1 }
    };

    const totalAcordado = caso.monto_total_acordado || 0;
    const totalPagado = totalAcordado - caso.saldo_pendiente;
    const progresoPago = totalAcordado > 0 ? (totalPagado / totalAcordado) * 100 : 0;

    return (
        <motion.div 
            className={styles.card}
            variants={contentVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
        >
            <div className={styles.summaryGrid}>
                <motion.div className={styles.summaryItem} variants={itemVariants}>
                    <div className={`${styles.summaryIconWrapper} ${styles.totalIcon}`}>
                        <DollarSign />
                    </div>
                    <div className={styles.summaryTextContainer}>
                        <span className={styles.summaryLabel}>Monto Acordado</span>
                        <span className={styles.summaryValue}>{`S/${totalAcordado.toLocaleString('es-PE', { minimumFractionDigits: 2 })}`}</span>
                    </div>
                </motion.div>
                <motion.div className={styles.summaryItem} variants={itemVariants}>
                    <div className={`${styles.summaryIconWrapper} ${styles.paidIcon}`}>
                        <CheckCircle />
                    </div>
                    <div className={styles.summaryTextContainer}>
                        <span className={styles.summaryLabel}>Total Pagado</span>
                        <span className={`${styles.summaryValue} ${styles.paidValue}`}>{`S/${totalPagado.toLocaleString('es-PE', { minimumFractionDigits: 2 })}`}</span>
                    </div>
                </motion.div>
                <motion.div className={styles.summaryItem} variants={itemVariants}>
                    <div className={`${styles.summaryIconWrapper} ${styles.pendingIcon}`}>
                        <Clock />
                    </div>
                    <div className={styles.summaryTextContainer}>
                        <span className={styles.summaryLabel}>Saldo Pendiente</span>
                        <span className={`${styles.summaryValue} ${styles.pendingValue}`}>{`S/${caso.saldo_pendiente.toLocaleString('es-PE', { minimumFractionDigits: 2 })}`}</span>
                    </div>
                </motion.div>
            </div>

            <motion.div className={styles.progressBarContainer} variants={itemVariants}>
                <motion.div 
                    className={styles.progressBar} 
                    initial={{ width: 0 }}
                    animate={{ width: `${progresoPago}%` }}
                    transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                />
            </motion.div>
            
            <div className={styles.separator}></div>

            <div className={styles.listHeader}>
                <h4 className={styles.listTitle}>Historial de Pagos</h4>
                <motion.button 
                    className={styles.addButton} 
                    onClick={onAddPago}
                    whileHover={{ y: -3, boxShadow: '0 8px 25px -8px rgba(0, 119, 182, 0.5)'}}
                    whileTap={{ scale: 0.97 }}
                >
                    <PlusCircle size={18}/>
                    <span>Registrar Pago</span>
                </motion.button>
            </div>

            <div className={styles.paymentList}>
                {caso.pagos.length > 0 ? (
                    caso.pagos.map((pago, index) => (
                        <motion.div 
                            key={pago.id} 
                            className={styles.paymentItem}
                            custom={index}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.1 }}
                        >
                            <div className={styles.paymentIcon}>
                                <DollarSign size={20} />
                            </div>
                            <div className={styles.paymentDetails}>
                                <span className={styles.paymentDescription}>{pago.descripcion || 'Pago registrado'}</span>
                                <span className={styles.paymentDate}>
                                    {new Date(pago.fecha_pago + 'T00:00:00').toLocaleDateString('es-PE', { year: 'numeric', month: 'long', day: 'numeric' })}
                                </span>
                            </div>
                            <span className={styles.paymentAmount}>{`S/ ${pago.monto_pagado.toLocaleString('es-PE', { minimumFractionDigits: 2 })}`}</span>
                        </motion.div>
                    ))
                ) : (
                    <div className={styles.emptyList}>
                        <p>No hay pagos registrados para este caso.</p>
                        <span>Haz clic en el botón para añadir el primer pago.</span>
                    </div>
                )}
            </div>
        </motion.div>
    );
};